import React, { useEffect, useRef } from 'react';
import { Button } from 'react-bootstrap';
import { formatShortDateTime } from '../../utils/dateFormatter';
import { CATEGORY_LABELS, SEVERITY_LABELS, TEXT } from '../../config/complaintLabels';
import ComplaintDetail from './ComplaintDetail';

/**
 * One guest complaint (tasks/followup-tasks-only.md Part B). Under its words, the two steps still ahead of it —
 * the action (a task, or «لا تحتاج مهمة») and closing — each in the server's terms: which step is live
 * (`item.step`) and its one clock (`item.clock`, B3): on time until a moment, or late since one. Only the step
 * waiting on ops is coloured. «فتح التفاصيل» opens the steps. Which card is open is the page's (`?id=`); the open
 * one is pinned above the lists, so it survives the complaint changing lists.
 */

/** The clock's deadline in words — instants from the server, shown in the business timezone. */
export const clockText = (clock) => {
  if (!clock) return null;
  if (clock.status === 'OVERDUE') return `${TEXT.overdueSince} ${formatShortDateTime(clock.since)}`;
  if (clock.status === 'ON_TIME') return `${TEXT.until} ${formatShortDateTime(clock.dueAt)}`;
  return null;
};

const Step = ({ name, state, clock, className }) => (
  <li className={`cp-step cp-step--${className}`}>
    <span className="cp-step-name">{name}</span>
    {state && <span className="cp-step-state">{state}</span>}
    {clockText(clock) && <span className="cp-step-due">{clockText(clock)}</span>}
  </li>
);

// The live step takes the clock's colour (purple on time, red late); the others stay quiet.
const liveClass = (clock, onTime) => (clock.status === 'OVERDUE' ? 'overdue' : onTime);

/** The tickets carrying the work, each isolated so a run of Latin ids keeps its order in Arabic text. */
const OpenTasks = ({ tickets }) => (
  <>
    {TEXT.actionTaskOpen}:{' '}
    {tickets.map((ticket, index) => (
      <React.Fragment key={ticket}>{index > 0 && '، '}<bdi>{ticket}</bdi></React.Fragment>
    ))}
  </>
);

// How a closed complaint's action step reads — from the server's record of how it closed (`closeOutcome`). One
// closed before the three steps has none, and shows none.
const CLOSED_ACTION = {
  no_task: TEXT.noTaskNeeded, change: TEXT.actionTasksDone, no_change: TEXT.actionTasksDone, tasks_done: TEXT.actionTasksDone
};

/**
 * The two steps for the server's step: [action, close]. Null for a step this page does not know (read
 * defensively — API_DATA_FETCHING_PATTERNS: a page may meet a server from another release).
 */
function stepsOf(item) {
  const clock = item.clock || {};
  switch (item.step) {
    case 'action':
      return [
        { state: TEXT.actionNeeded, clock, className: liveClass(clock, 'on_time') },
        { state: TEXT.closeAfterAction, className: 'waiting' }
      ];
    case 'in_progress':
      return [
        { state: <OpenTasks tickets={clock.openTasks || []} />, className: 'waiting' },
        { state: TEXT.closeLater, className: 'waiting' }
      ];
    case 'close':
      return [
        { state: TEXT.actionTasksDone, className: 'done' },
        { state: TEXT.readyToClose, clock, className: liveClass(clock, 'ready') }
      ];
    case 'closed':
      return [
        { state: CLOSED_ACTION[item.closeOutcome] || null, className: 'done' },
        { state: `${TEXT.closedOn} ${formatShortDateTime(item.closedAt)}`, className: 'done' }
      ];
    default:
      return null;
  }
}

const ComplaintCard = ({ item, defaults, options, open, onToggle }) => {
  const ref = useRef(null);
  // An open card is brought into view — a link target, or a complaint an action just moved to this list.
  useEffect(() => {
    if (open && ref.current && ref.current.scrollIntoView) ref.current.scrollIntoView({ block: 'nearest' });
  }, [open]);
  const steps = stepsOf(item);

  return (
    <article ref={ref} className={`gr-card ${open ? 'cp-card--open' : ''}`}>
      <header className="gr-card-head">
        <div className="gr-card-title">
          <span className="gr-unit">{item.unit && item.unit.name}</span>
          {item.unit && item.unit.code && <span className="gr-code" dir="ltr">{item.unit.code}</span>}
          <span className="cp-category">{CATEGORY_LABELS[item.category] || item.category}</span>
          <span className={`cp-sev cp-sev--${item.severity}`}>{SEVERITY_LABELS[item.severity]}</span>
        </div>
        {item.overdue && <span className="cp-late">{TEXT.late}</span>}
      </header>

      <p className="cp-desc" dir="auto">{item.description}</p>

      {steps && (
        <ol className="cp-loop" aria-label={TEXT.loopLabel}>
          <Step name={TEXT.stepAction} {...steps[0]} />
          <Step name={TEXT.stepClose} {...steps[1]} />
        </ol>
      )}

      <div className="cp-meta">
        {item.booking.guestName && <span dir="auto">{item.booking.guestName}</span>}
        {item.booking.code && <span>{TEXT.booking} <bdi>{item.booking.code}</bdi></span>}
      </div>

      <div className="gr-actions">
        <Button variant="link" className="touch-btn gr-link" onClick={onToggle} aria-expanded={open}>
          {open ? TEXT.hideDetails : TEXT.showDetails}
        </Button>
      </div>

      {open && <ComplaintDetail item={item} defaults={defaults} options={options} />}
    </article>
  );
};

export default ComplaintCard;
